/**
 * Central v2-to-v1 enum normalizer.
 *
 * The AI agent writes component data using v2 JSON schema enum values
 * (lowercase / snake_case). The frontend inline UI expects v1 enum values
 * (uppercase / different naming). This module maps v2 values back to v1 at
 * the store boundary so the UI doesn't need to change.
 *
 * Values already in v1 format pass through unchanged.
 */

const V2_TO_V1 = {
  document_editor: {
    sourceType: { editor: 'EDITOR', docx: 'DOCX', url: 'URL' },
    exportFormat: { docx: 'DOCX', pdf: 'PDF' },
  },
  merge_workflow: {
    mode: { all: 'ALL', any: 'ANY', or_and: 'OROFAND' },
  },
  time_delay: {
    mode: {
      elapsed: 'TIME_ELAPSED',
      specific_datetime: 'CERTAIN_DATE_TIME',
      day_of_week: 'CERTAIN_WEEK_DAY',
      placeholder_datetime: 'AT_DATE_TIME_PLACEHOLDER',
    },
    dayOfWeek: { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' },
  },
  trigger_on_database_update: {
    triggerType: {
      record_created: 'TRIGGER_TYPE_RECORD_CREATED',
      any_update: 'TRIGGER_TYPE_ANY_UPDATE',
      specific_column: 'TRIGGER_TYPE_SPECIFIC_COLUMN',
    },
  },
  conditional_workflow: {
    function: {
      equal: 'EQUAL', not_equal: 'NOTEQUAL',
      contains: 'CONTAIN', not_contains: 'NOTCONTAIN',
      has_selected: 'HAS_SELECTED', has_not_selected: 'HAS_NOT_SELECTED',
      matches: 'MATCHES', not_matches: 'NOTMATCH',
      greater_than: 'GREATERTHAN', greater_than_or_equal: 'GREATERTHANOREQUAL',
      less_than: 'LESSTHAN', less_than_or_equal: 'LESSTHANOREQUAL',
      not_empty: 'NOTEMPTY', empty: 'EMPTY',
    },
    logic: { and: 'AND', or: 'OR' },
    result: { continue: 'CONTINUE', stop: 'STOP' },
    defaultResult: { continue: 'CONTINUE', stop: 'STOP' },
  },
  schedule_start: {
    'stopCondition.type': { after_count: 'after_a_certain_number_of_repeats', on_date: 'once_a_date_time_is_reached' },
    'repeat.unit': { businessDay: 'businessDays' },
  },
  code: {
    language: { python_3_9: 'Python_3_9', nodejs_16: 'NodeJS_16', nodejs_18: 'NodeJS_16' },
  },
  trigger_from_api_polling: {
    responseFormat: { json: 'JSON', xml: 'XML', text: 'TEXT' },
    'pollingInterval.unit': { minute: 'minutes', hour: 'hours', day: 'days' },
  },
  await_external_event: {
    pollingResponseFormat: { json: 'JSON', xml: 'XML', text: 'TEXT' },
    'pollingInterval.type': { minute: 'minutes', hour: 'hours', day: 'days' },
    'timeout.type': { minute: 'minutes', hour: 'hours', day: 'days' },
  },
};

function applyFlat(data, fieldName, valueMap) {
  if (fieldName in data) {
    const cur = data[fieldName];
    if (typeof cur === 'string' && cur in valueMap) {
      data[fieldName] = valueMap[cur];
    }
  }
}

function applyDotPath(data, dotPath, valueMap) {
  const parts = dotPath.split('.');
  let obj = data;
  for (let i = 0; i < parts.length - 1; i++) {
    if (obj == null || typeof obj !== 'object') return;
    obj = obj[parts[i]];
  }
  const leaf = parts[parts.length - 1];
  if (obj != null && typeof obj === 'object' && leaf in obj) {
    const cur = obj[leaf];
    if (typeof cur === 'string' && cur in valueMap) {
      obj[leaf] = valueMap[cur];
    }
  }
}

/**
 * Recursively walk arrays/objects and apply field-level mappings wherever
 * a mapped field name appears. Handles the deeply nested conditional_workflow
 * rule structure (arrays of objects containing `function`, `logic`, etc.).
 */
function applyDeep(obj, fieldMappings) {
  if (obj == null || typeof obj !== 'object') return;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      applyDeep(item, fieldMappings);
    }
    return;
  }

  for (const [fieldName, valueMap] of Object.entries(fieldMappings)) {
    if (fieldName.includes('.')) continue;
    if (fieldName in obj) {
      const cur = obj[fieldName];
      if (typeof cur === 'string' && cur in valueMap) {
        obj[fieldName] = valueMap[cur];
      }
    }
  }

  for (const val of Object.values(obj)) {
    if (val != null && typeof val === 'object') {
      applyDeep(val, fieldMappings);
    }
  }
}

/**
 * Normalize v2 schema enum values to v1 frontend enum values.
 *
 * @param {string} componentType - e.g. 'document_editor', 'merge_workflow'
 * @param {object} rawData - the extracted component data (will be mutated)
 * @returns {object} the same rawData object, with mapped values replaced
 */
export function normalizeV2ToV1(componentType, rawData) {
  if (!rawData || typeof rawData !== 'object') return rawData;

  const fieldMappings = V2_TO_V1[componentType];
  if (!fieldMappings) return rawData;

  const hasDeepFields = componentType === 'conditional_workflow';

  for (const [fieldName, valueMap] of Object.entries(fieldMappings)) {
    if (fieldName.includes('.')) {
      applyDotPath(rawData, fieldName, valueMap);
    } else if (!hasDeepFields) {
      applyFlat(rawData, fieldName, valueMap);
    }
  }

  if (hasDeepFields) {
    applyDeep(rawData, fieldMappings);
  }

  return rawData;
}
